import { z } from 'zod';

export const inventoryItemSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, 'Name is required'),
  category: z.string(),
  quantity: z.number().min(0, 'Quantity cannot be negative'),
  unit: z.string(),
  minThreshold: z.number().min(0, 'Threshold cannot be negative'),
  minExpirationThreshold: z.number().min(0, 'Expiration threshold cannot be negative').optional(),
  batchNumber: z.string().optional(),
  expirationDate: z.union([z.string(), z.date()]).optional().transform((val) => {
    if (!val) return undefined;
    if (val instanceof Date) return val;
    return new Date(val);
  }),
  location: z.string().optional(),
  notes: z.string().optional(),
  lastUpdated: z.string().optional(),
  supplier: z.string().optional(),
  reorderQuantity: z.number().optional(),
});

export type InventoryItem = z.infer<typeof inventoryItemSchema>;

// Flexible history tracking that supports arbitrary fields
export const fieldChangeSchema = z.object({
  field: z.string(), // The name of the changed field
  oldValue: z.unknown(), // Previous value of any type
  newValue: z.unknown(), // New value of any type
  displayName: z.string().optional(), // Human readable field name
});

export type FieldChange = z.infer<typeof fieldChangeSchema>;

export const inventoryHistoryEntrySchema = z.object({
  id: z.string(),
  itemId: z.string(),
  timestamp: z.date(),
  userId: z.string(),
  userName: z.string(),
  changes: z.array(fieldChangeSchema), // Array of field changes
  reason: z.string().optional(), // Optional reason for the change
  metadata: z.record(z.unknown()).optional(), // Additional metadata about the change
});

export type InventoryHistoryEntry = z.infer<typeof inventoryHistoryEntrySchema>;

export const inventoryHistoryResponseSchema = z.object({
  entries: z.array(inventoryHistoryEntrySchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  hasMore: z.boolean(),
});

export type InventoryHistoryResponse = z.infer<typeof inventoryHistoryResponseSchema>;

// Helper type for grouping changes by date
export interface GroupedHistoryEntries {
  date: string;
  entries: InventoryHistoryEntry[];
}
